import React, { useState, useCallback, useRef, useEffect } from "react";
import { Alert, Button, Collapse } from "antd";
import { IMsalContext } from "@azure/msal-react";
import { TransferTab } from "./types";
import NewTransferTab from "./NewTransferTab";
import ActiveTransferTab from "./ActiveTransferTab";
import PastExecutionTab from "./PastExecutionTab";
import "./style.scss";

const SUCCESS_DURATION_MS = 20_000;

export interface DataTransferSectionProps {
  /** Display name of the currently opened space — locked as destination. */
  spaceName: string;
  /** Canonical space identifier (space_name) — sent to the API; falls back to spaceName. */
  spaceApiName?: string | null;
  /** S3 bucket id for the opened space — used to pre-fill the destination URI. */
  destinationBucket?: string | null;
  /** Region of the opened space — used to filter source spaces. */
  region?: string | null;
  /** Domain of the opened space — drives which spaces are selectable as source. */
  domainName?: string | null;
  /** MSAL instance for authenticated API calls. */
  msalInstance: IMsalContext;
}

const TABS: { key: TransferTab; label: string }[] = [
  { key: "new", label: "New Transfer" },
  { key: "active", label: "Active Transfer" },
  { key: "past", label: "Past Execution" },
];

const DataTransferSection: React.FC<DataTransferSectionProps> = ({
  spaceName,
  spaceApiName,
  destinationBucket,
  region,
  domainName,
  msalInstance,
}) => {
  const [activeTab, setActiveTab] = useState<TransferTab>("new");
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearSuccess = useCallback(() => {
    if (successTimerRef.current) {
      clearTimeout(successTimerRef.current);
      successTimerRef.current = null;
    }
    setSuccessMsg(null);
  }, []);

  const handleTransferSuccess = useCallback(
    (msg: string) => {
      clearSuccess();
      setSuccessMsg(msg);
      setActiveTab("active");
      successTimerRef.current = setTimeout(() => {
        setSuccessMsg(null);
        successTimerRef.current = null;
      }, SUCCESS_DURATION_MS);
    },
    [clearSuccess],
  );

  const handleNewTransfer = useCallback(() => {
    clearSuccess();
    setActiveTab("new");
  }, [clearSuccess]);

  useEffect(() => () => clearSuccess(), [clearSuccess]);

  return (
    <div className="dt-section-wrapper" style={{ backgroundColor: "#f5f5f5" }}>
      <Collapse
        defaultActiveKey={["dt"]}
        style={{ backgroundColor: "#f5f5f6" }}
      >
        <Collapse.Panel
          key="dt"
          header={
            <span>
              Data Transfer inside <strong>{spaceName}</strong>
            </span>
          }
        >
          {/* Tab bar */}
          <div className="dt-tab-bar">
            {TABS.map(t => (
              <button
                key={t.key}
                className={`dt-tab-btn${
                  activeTab === t.key ? " dt-tab-btn--active" : ""
                }`}
                onClick={() => setActiveTab(t.key)}
                type="button"
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="dt-tab-content">
            {successMsg && (
              <Alert
                style={{ marginBottom: 16 }}
                type="success"
                showIcon
                closable
                onClose={clearSuccess}
                message="Submitted"
                description={successMsg}
                action={
                  <Button size="small" type="link" onClick={handleNewTransfer}>
                    New transfer
                  </Button>
                }
              />
            )}

            {activeTab === "new" && (
              <NewTransferTab
                openedSpace={spaceName}
                openedSpaceApiName={spaceApiName}
                openedSpaceBucket={destinationBucket}
                region={region}
                domainName={domainName}
                msalInstance={msalInstance}
                onTransferSuccess={handleTransferSuccess}
              />
            )}
            {activeTab === "active" && (
              <ActiveTransferTab
                spaceName={spaceName}
                spaceApiName={spaceApiName}
                msalInstance={msalInstance}
              />
            )}
            {activeTab === "past" && (
              <PastExecutionTab
                spaceName={spaceName}
                spaceApiName={spaceApiName}
                msalInstance={msalInstance}
              />
            )}
          </div>
        </Collapse.Panel>
      </Collapse>
    </div>
  );
};

export default DataTransferSection;
